import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import backendApi from "../api/backendApi";
import { protectedTheme } from "../components/ui/protectedTheme";

const STATUS_OPTIONS = ["INITIATED", "SUCCESS", "FAILED", "CANCELLED", "EXPIRED"];

const createEmptyFilters = () => ({
  transactionId: "",
  payerName: "",
  aimsNo: "",
  paymentMonth: "",
  paidFrom: "",
  paidTo: "",
  status: "",
});

const inputClass = protectedTheme.input;

function fmtAmount(value) {
  const amount = Number(value ?? 0);
  return amount.toLocaleString("en-MY", {
    style: "currency",
    currency: "MYR",
  });
}

function formatPaymentMonth(value) {
  if (!value) return "-";

  const [year, month] = value.split("-");
  const parsedYear = Number(year);
  const parsedMonth = Number(month);

  if (!parsedYear || !parsedMonth) return value;

  const date = new Date(parsedYear, parsedMonth - 1, 1);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-US", { month: "long", year: "numeric" });
}

function formatPaidAt(value) {
  if (!value) return "N/A";

  return new Date(value).toLocaleString("en-MY", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Kuala_Lumpur",
  });
}

function formatPayerName(payer = {}) {
  return [payer.firstName, payer.lastName].filter(Boolean).join(" ") || "-";
}

function getErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof data === "string") return data;
  return data?.message || error.message || "Failed to load transactions";
}

function compactFilters(filters) {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => String(value || "").trim())
  );
}

function StatusPill({ value }) {
  const status = (value || "").toUpperCase();
  const className =
    {
      SUCCESS: "bg-green-100 text-green-700",
      FAILED: "bg-red-100 text-red-700",
      CANCELLED: "bg-orange-100 text-orange-700",
      EXPIRED: "bg-gray-200 text-gray-700",
      INITIATED: "bg-amber-100 text-amber-700",
      PENDING: "bg-blue-100 text-blue-700",
    }[status] || "bg-zinc-100 text-zinc-700";

  return (
    <span className={`rounded-full px-2 py-1 text-xs ${className}`}>
      {status || "-"}
    </span>
  );
}

function FilterField({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm font-medium text-zinc-700">
      <span>{label}</span>
      {children}
    </label>
  );
}

function TransactionMobileCard({ transaction, onOpen }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-2xl bg-white p-4 text-left shadow transition hover:-translate-y-0.5 hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-zinc-900">
            {transaction.paymentReference || "NA"}
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            {formatPayerName(transaction.payer)}
          </p>
        </div>
        <StatusPill value={transaction.status} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Payment Month
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {formatPaymentMonth(transaction.paymentMonth)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Amount
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {fmtAmount(transaction.amount)}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            AIMS No
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {transaction.payer?.aimsNo || "-"}
          </p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wide text-zinc-400">
            Paid At
          </p>
          <p className="mt-1 font-medium text-zinc-800">
            {formatPaidAt(transaction.paymentDate)}
          </p>
        </div>
      </div>
    </button>
  );
}

const AdminTransactionPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [draftFilters, setDraftFilters] = useState(createEmptyFilters);
  const [filters, setFilters] = useState(createEmptyFilters);

  const totalPages = useMemo(() => Math.ceil(total / size) || 1, [total, size]);
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const updateDraftFilter = (name, value) => {
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(0);
    setFilters({ ...draftFilters });
  };

  const clearFilters = () => {
    setDraftFilters(createEmptyFilters());
    setFilters(createEmptyFilters());
    setPage(0);
  };

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const res = await backendApi.get("/payment/transactions", {
          params: {
            page,
            size,
            ...compactFilters(filters),
          },
        });
        const data = res?.data;
        const content = Array.isArray(data?.content)
          ? data.content
          : Array.isArray(data)
          ? data
          : [];
        const totalElements =
          typeof data?.totalElements === "number"
            ? data.totalElements
            : content.length;

        if (!ignore) {
          setRows(content);
          setTotal(totalElements);
        }
      } catch (e) {
        if (!ignore) {
          setRows([]);
          setTotal(0);
          setError(getErrorMessage(e));
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    load();

    return () => {
      ignore = true;
    };
  }, [page, size, filters]);

  return (
    <div className={protectedTheme.page}>
      <div className={protectedTheme.shell}>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className={protectedTheme.eyebrow}>Admin</p>
          <h1 className={`${protectedTheme.title} mt-1`}>
            Transactions
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Search by reference, payer details, reporting month, paid date, and
            status.
          </p>
        </div>
        <div className="text-sm text-zinc-500">
          Showing {rows.length} of {total} transaction{total === 1 ? "" : "s"}
        </div>
      </div>

      <form
        onSubmit={applyFilters}
        className={protectedTheme.card}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <FilterField label="Transaction ID">
            <input
              type="search"
              value={draftFilters.transactionId}
              onChange={(e) =>
                updateDraftFilter("transactionId", e.target.value)
              }
              placeholder="e.g. ORD-2026..."
              className={inputClass}
            />
          </FilterField>

          <FilterField label="Payer Name">
            <input
              type="search"
              value={draftFilters.payerName}
              onChange={(e) => updateDraftFilter("payerName", e.target.value)}
              placeholder="First, last, or full name"
              className={inputClass}
            />
          </FilterField>

          <FilterField label="AIMS No">
            <input
              type="search"
              value={draftFilters.aimsNo}
              onChange={(e) => updateDraftFilter("aimsNo", e.target.value)}
              placeholder="AIMS number"
              className={inputClass}
            />
          </FilterField>

          <FilterField label="Payment Month">
            <input
              type="month"
              value={draftFilters.paymentMonth}
              onChange={(e) =>
                updateDraftFilter("paymentMonth", e.target.value)
              }
              className={inputClass}
            />
          </FilterField>

          <FilterField label="Paid From">
            <input
              type="date"
              value={draftFilters.paidFrom}
              onChange={(e) => updateDraftFilter("paidFrom", e.target.value)}
              className={inputClass}
            />
          </FilterField>

          <FilterField label="Paid To">
            <input
              type="date"
              value={draftFilters.paidTo}
              onChange={(e) => updateDraftFilter("paidTo", e.target.value)}
              className={inputClass}
            />
          </FilterField>

          <FilterField label="Status">
            <select
              value={draftFilters.status}
              onChange={(e) => updateDraftFilter("status", e.target.value)}
              className={inputClass}
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </FilterField>

          <FilterField label="Page Size">
            <select
              value={size}
              onChange={(e) => {
                setPage(0);
                setSize(Number(e.target.value));
              }}
              className={inputClass}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/page
                </option>
              ))}
            </select>
          </FilterField>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-zinc-500">
            {activeFilterCount > 0
              ? `${activeFilterCount} filter${
                  activeFilterCount === 1 ? "" : "s"
                } applied`
              : "No filters applied"}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={clearFilters}
              className={protectedTheme.secondaryButton}
            >
              Clear
            </button>
            <button
              type="submit"
              className={protectedTheme.primaryButton}
              disabled={loading}
            >
              Search Transactions
            </button>
          </div>
        </div>
      </form>

      <div className="hidden rounded-2xl bg-white shadow md:block">
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-zinc-50 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Transaction ID</th>
                <th className="px-4 py-3 font-medium">Payer</th>
                <th className="px-4 py-3 font-medium">Payment Month</th>
                <th className="px-4 py-3 font-medium">Paid At</th>
                <th className="px-4 py-3 font-medium">Amount (RM)</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td className="px-4 py-6" colSpan={6}>
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && error && (
                <tr>
                  <td className="px-4 py-6 text-red-600" colSpan={6}>
                    {error}
                  </td>
                </tr>
              )}
              {!loading && !error && rows?.length === 0 && (
                <tr>
                  <td className="px-4 py-6" colSpan={6}>
                    No transactions found.
                  </td>
                </tr>
              )}
              {!loading &&
                !error &&
                rows?.map((transaction) => (
                  <tr
                    key={transaction.id}
                    className="cursor-pointer border-t hover:bg-zinc-50"
                    onClick={() => navigate(`/transaction/${transaction.id}`)}
                  >
                    <td className="px-4 py-3">
                      {transaction.paymentReference || "NA"}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col">
                        <span className="font-medium">
                          {formatPayerName(transaction.payer)}
                        </span>
                        <span className="text-xs text-zinc-500">
                          {transaction.payer?.aimsNo || ""}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {formatPaymentMonth(transaction.paymentMonth)}
                    </td>
                    <td className="px-4 py-3">
                      {formatPaidAt(transaction.paymentDate)}
                    </td>
                    <td className="px-4 py-3">
                      {fmtAmount(transaction.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill value={transaction.status} />
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-3 md:hidden">
        {loading && (
          <div className="rounded-2xl bg-white p-4 text-sm shadow">
            Loading...
          </div>
        )}
        {!loading && error && (
          <div className="rounded-2xl bg-white p-4 text-sm text-red-600 shadow">
            {error}
          </div>
        )}
        {!loading && !error && rows?.length === 0 && (
          <div className="rounded-2xl bg-white p-4 text-sm shadow">
            No transactions found.
          </div>
        )}
        {!loading &&
          !error &&
          rows?.map((transaction) => (
            <TransactionMobileCard
              key={transaction.id}
              transaction={transaction}
              onOpen={() => navigate(`/transaction/${transaction.id}`)}
            />
          ))}
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-zinc-500">
          Page {page + 1} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            className="rounded-lg border bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={page === 0 || loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
          >
            Prev
          </button>
          <button
            className="rounded-lg border bg-white px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={page + 1 >= totalPages || loading}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

export default AdminTransactionPage;
